// An organizer's schedule file or Google Sheet becomes an event's games: the
// format, the source and its tab, the model's reading, a preview to check and
// fix, then one Apply. Without a tournament the Apply creates the event too;
// with one, a dry run shows what the re-import adds, changes and removes first

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import Card from './Card';
import Fieldset from './Fieldset';
import Input from './Input';
import RadioButton from './RadioButton';
import Select from './Select';
import Spinner from './Spinner';
import TournamentCardEdit from './TournamentCardEdit';
import ScheduleImportPreview from './ScheduleImportPreview';
import ScheduleImportDiff from './ScheduleImportDiff';
import { useModal } from './ModalProvider';
import { useBackBtn, useTournamentParts } from '../utils/hooks';
import {
  googleConnect,
  googleDisconnect,
  googleStatus,
  tournamentScheduleImportParse,
  tournamentScheduleImportApply,
} from '../utils/firebase';
import {
  isGoogleConfigured,
  pickGoogleFile,
  prepareGoogleSignIn,
  requestGoogleCode,
} from '../utils/google';
import { defaultTimeZone, scheduleImportMaxBytes } from '../utils/consts';
import './ScheduleImport.scss';

// The backend's format registry, as far as the picker needs it
const formats = [{ id: 'sd7', name: 'SD7', extensions: ['xlsx'] }];

const sourceKinds = [
  { id: 'file', name: 'File' },
  { id: 'gsheet', name: 'Google Sheet link' },
];

// The callable says nothing until the model is done: the bar is a clock
const expectedReadSeconds = 120;

const readBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read the file'));
    reader.readAsDataURL(file);
  });

// The backend's importKey: the dry run and the checks name games by it
const keyOf = (game, fieldNames) =>
  `${game.date}|${fieldNames.get(game.field) ?? game.field}|${game.start}`;

// Checks name games by key; the preview needs their places in the list
const indexChecks = (checks, games) => {
  const at = new Map();
  games.forEach((game, i) =>
    at.set(game.key, [...(at.get(game.key) ?? []), i]),
  );
  return (checks ?? []).map((check) => ({
    ...check,
    games: [...new Set((check.keys ?? []).flatMap((key) => at.get(key) ?? []))],
  }));
};

// The tab the event came from, else the first one that holds games
const defaultSheet = (sheets, previous) => {
  const selectable = sheets.filter((s) => s.kind !== 'refs');
  if (selectable.some((s) => s.name === previous)) return previous;
  return (
    selectable.find((s) => s.kind === 'games')?.name ??
    selectable[0]?.name ??
    ''
  );
};

// One tab is one day, so the day goes into the suggested name
const suggestedEvent = ({ event }) => ({
  name: [event.name, event.date && moment(event.date).format('M/D')]
    .filter(Boolean)
    .join(' '),
  location: event.venue,
  startDate: event.date,
  endDate: event.date,
  timezone: defaultTimeZone,
});

const changesText = ({ created, updated, deleted }) =>
  [
    created && `${created} added`,
    updated && `${updated} changed`,
    deleted && `${deleted} removed`,
  ]
    .filter(Boolean)
    .join(', ');

function ReadingProgress({ sheet }) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const share = Math.min(95, (seconds / expectedReadSeconds) * 100);

  return (
    <div className='reading' role='status'>
      <div className='bar'>
        <div className='done' style={{ width: `${share}%` }} />
      </div>
      <p>
        Reading <span className='font-medium'>{sheet}</span>:{' '}
        {moment.utc(seconds * 1000).format('m:ss')}. A hundred games take about
        two minutes; keep this page open.
      </p>
    </div>
  );
}

export default function ScheduleImport({ tournament }) {
  const id = tournament?.id ?? null;
  const navigate = useNavigate();
  const handleBack = useBackBtn();
  const { openModal } = useModal();
  const [searchParams] = useSearchParams();
  // The diff names the stored games a re-import changes or removes
  const arePartsReady = useTournamentParts(id, ['games', 'fields']);
  const eventSheet = tournament?.source?.kind === 'gsheet';

  const [format, setFormat] = useState(formats[0].id);
  const [sourceKind, setSourceKind] = useState(eventSheet ? 'gsheet' : 'file');
  const [link, setLink] = useState('');
  const [source, setSource] = useState(null);
  const [sourceName, setSourceName] = useState('');
  // A sheet nobody could read: what the Picker needs, and the source to retry
  const [access, setAccess] = useState(null);
  const [googleAccount, setGoogleAccount] = useState(null);
  const [isGranting, setIsGranting] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState('');
  const [isListing, setIsListing] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [unchanged, setUnchanged] = useState(false);
  // Game index → {division?, start?}: the preview's fixes of the reading
  const [fixes, setFixes] = useState({});
  const [dryRun, setDryRun] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [dryRunTick, setDryRunTick] = useState(0);
  // Game ids whose removal, with their referees, was confirmed
  const [confirmed, setConfirmed] = useState(() => new Set());
  const fileInputRef = useRef(null);
  // The new event's form values, which a dry run needs and nothing renders
  const eventRef = useRef(null);
  // A late answer to a superseded request, or to a page left, is dropped
  const requestRef = useRef(0);
  const dryRunRef = useRef(0);

  useEffect(() => {
    const requests = [requestRef, dryRunRef];
    return () =>
      requests.forEach((ref) => {
        ref.current += 1;
      });
  }, []);

  const formatInfo = formats.find((f) => f.id === format);
  const busy = isListing || isReading || isApplying || isGranting;
  const selectableSheets = sheets.filter((s) => s.kind !== 'refs');
  const hasRefsTab = sheets.some((s) => s.kind === 'refs');
  const isSheet = sourceKind === 'gsheet';

  const clearDraft = () => {
    setParsed(null);
    setUnchanged(false);
    setFixes({});
    setDryRun(null);
    setConfirmed(new Set());
  };

  const clearSource = () => {
    setSource(null);
    setSourceName('');
    setAccess(null);
    setSheets([]);
    setSheet('');
    clearDraft();
  };

  const pickFormat = (value) => {
    requestRef.current += 1;
    setFormat(value);
    clearSource();
    setIsListing(false);
  };

  const pickSourceKind = (value) => {
    requestRef.current += 1;
    setSourceKind(value);
    clearSource();
    setIsListing(false);
  };

  // A sheet nobody could read offers the way to grant it; any other refusal
  // is the toast alone
  const showError = (error, picked, fallback) => {
    toast.error(error?.message ?? fallback);
    if (error?.details?.reason === 'googleAccess')
      setAccess({ ...error.details, source: picked });
  };

  // Without a tab the callable only lists them: no model call. The listed
  // source and its default tab, or null when it failed or was superseded
  const listTabs = async (makeSource, fallback) => {
    const request = ++requestRef.current;
    clearSource();
    setIsListing(true);
    let picked = null;
    try {
      picked = await makeSource();
      const { data } = await tournamentScheduleImportParse({
        ...(id && { id }),
        format,
        source: picked,
      });
      if (request !== requestRef.current) return null;
      const tab = defaultSheet(
        data.sheets,
        data.linkedSheet ?? tournament?.source?.sheet,
      );
      setSource(picked);
      setSourceName(data.name ?? picked.name ?? '');
      setSheets(data.sheets);
      setSheet(tab);
      return { picked, tab };
    } catch (error) {
      console.error(error);
      if (request === requestRef.current) showError(error, picked, fallback);
      return null;
    } finally {
      if (request === requestRef.current) setIsListing(false);
    }
  };

  const openLink = () =>
    listTabs(
      () => ({ kind: 'gsheet', url: link.trim() }),
      'Failed to open the sheet',
    );

  // No link: the backend reads the sheet the event was imported from
  const openEventSheet = () =>
    listTabs(() => ({ kind: 'gsheet' }), 'Failed to open the sheet');

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    // The same file picked again, after an edit to it, is a new pick
    e.target.value = '';
    if (!file) return;
    const extension = file.name.split('.').pop().toLowerCase();
    if (!formatInfo.extensions.includes(extension)) {
      toast.error(
        `${formatInfo.name} schedules are .${formatInfo.extensions.join(', .')} files`,
      );
      return;
    }
    if (file.size > scheduleImportMaxBytes) {
      toast.error('The file is larger than 5 MB');
      return;
    }

    await listTabs(
      async () => ({
        kind: 'file',
        name: file.name,
        base64: await readBase64(file),
      }),
      'Failed to open the file',
    );
  };

  const read = async (picked = source, tab = sheet) => {
    const request = ++requestRef.current;
    clearDraft();
    setIsReading(true);
    try {
      const { data } = await tournamentScheduleImportParse({
        ...(id && { id }),
        format,
        source: picked,
        sheet: tab,
      });
      if (request !== requestRef.current) return;
      if (data.unchanged) {
        setUnchanged(true);
        toast.info(
          picked.kind === 'gsheet'
            ? 'No changes: the tab holds what the event last imported'
            : 'No changes since the last import of this file and tab',
        );
        return;
      }
      setParsed(data);
    } catch (error) {
      console.error(error);
      if (request === requestRef.current)
        showError(error, picked, 'Failed to read the schedule');
    } finally {
      if (request === requestRef.current) setIsReading(false);
    }
  };

  // "Refresh from sheet" on the event lands here: its sheet is read at once,
  // and nothing is written before Apply
  useEffect(() => {
    if (searchParams.get('from') !== 'sheet' || !eventSheet) return;
    (async () => {
      const listed = await openEventSheet();
      if (listed && listed.tab === tournament.source.sheet)
        read(listed.picked, listed.tab);
    })();
    // Once, on arrival: a later change of the query is a new page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSheet || !isGoogleConfigured) return undefined;
    prepareGoogleSignIn();
    let isCurrent = true;
    (async () => {
      try {
        const { data } = await googleStatus({});
        if (isCurrent) setGoogleAccount(data);
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      isCurrent = false;
    };
  }, [isSheet]);

  // The popup comes first, straight from the click, or it may be blocked.
  // Picking the sheet in the Picker is what grants Muva access to it
  const grantSheet = async () => {
    const { fileId, connected, source: retry } = access;
    setIsGranting(true);
    try {
      let accessToken;
      if (connected) {
        const { data } = await googleStatus({ token: true });
        setGoogleAccount(data);
        if (!data.connected) {
          setAccess((prev) => ({ ...prev, connected: false }));
          toast.info('Google access has ended: connect Google again');
          return;
        }
        accessToken = data.accessToken;
      } else {
        const code = await requestGoogleCode();
        if (!code) return;
        const { data } = await googleConnect({ code });
        setGoogleAccount(data);
        accessToken = data.accessToken;
      }
      if (!(await pickGoogleFile({ fileId, accessToken }))) return;
      await listTabs(() => retry, 'Failed to open the sheet');
    } catch (error) {
      console.error(error);
      toast.error(error?.message ?? 'Failed to connect Google');
    } finally {
      setIsGranting(false);
    }
  };

  const disconnectGoogle = () => {
    const handleDisconnect = async () => {
      setIsGranting(true);
      try {
        const { data } = await googleDisconnect({});
        setGoogleAccount(data);
        toast.success('Google disconnected');
      } catch (error) {
        console.error(error);
        toast.error(error?.message ?? 'Failed to disconnect Google');
      } finally {
        setIsGranting(false);
      }
    };

    openModal({
      header: 'Disconnect Google',
      body: (
        <p className='font-16'>
          Disconnect{' '}
          <span className='font-medium'>
            {googleAccount?.email ?? 'your Google account'}
          </span>
          ? Muva loses access to the sheets you picked: their events can be
          refreshed and checked for changes only while the sheets are shared by
          link.
        </p>
      ),
      buttons: [
        { text: 'Cancel', className: 'bw' },
        { text: 'Disconnect', className: 'danger', onClick: handleDisconnect },
      ],
    });
  };

  const fieldNames = useMemo(
    () => new Map((parsed?.fields ?? []).map((f) => [f.key, f.name])),
    [parsed],
  );

  const draft = useMemo(() => {
    if (!parsed) return null;
    const games = parsed.games.map((game, i) => {
      const fix = fixes[i];
      if (!fix) return game;
      const fixed = { ...game, ...fix };
      return { ...fixed, key: keyOf(fixed, fieldNames) };
    });
    return { ...parsed, games };
  }, [parsed, fixes, fieldNames]);

  const fixCount = Object.keys(fixes).length;

  // A re-import is always dry-run, for the diff; a new event only once the
  // preview is fixed, since Apply re-runs the checks on the fixed draft
  const needsDryRun = !!draft && (!!id || fixCount > 0);
  useEffect(() => {
    const request = ++dryRunRef.current;
    if (!needsDryRun) {
      setDryRun(null);
      setIsChecking(false);
      return undefined;
    }
    setIsChecking(true);
    const timer = setTimeout(
      async () => {
        const event = id
          ? null
          : { ...suggestedEvent(draft), ...eventRef.current };
        try {
          const { data } = await tournamentScheduleImportApply({
            ...(id ? { id } : { event }),
            draft,
            dryRun: true,
          });
          if (request !== dryRunRef.current) return;
          setDryRun({ ...data, checks: indexChecks(data.checks, draft.games) });
        } catch (error) {
          console.error(error);
          if (request !== dryRunRef.current) return;
          const failed = error?.details?.checks;
          if (failed) {
            // Failing errors refuse even a dry run, and come back alone
            setDryRun({
              checks: indexChecks(failed, draft.games),
              failedOnly: true,
            });
          } else {
            const message = error?.message ?? 'Failed to check the schedule';
            toast.error(message);
            setDryRun({ error: message });
          }
        } finally {
          if (request === dryRunRef.current) setIsChecking(false);
        }
        // Fixes come in bursts; the first dry run of a re-import does not wait
      },
      fixCount > 0 ? 600 : 0,
    );
    return () => clearTimeout(timer);
  }, [needsDryRun, draft, id, fixCount, dryRunTick]);

  // The parse ran every check; a dry run re-runs all but those against the
  // sheet's cells, which keep the parse's answer
  const checks = useMemo(() => {
    if (!parsed) return [];
    const parseChecks = indexChecks(parsed.checks, parsed.games);
    const fresh = new Map((dryRun?.checks ?? []).map((c) => [c.code, c]));
    const rechecked = parseChecks.map((check) => {
      if (fresh.has(check.code)) return fresh.get(check.code);
      // A refusal returns the failing errors only: the other errors passed
      if (dryRun?.failedOnly && check.severity === 'error' && !check.passed)
        return { ...check, passed: true, message: 'Passes now', games: [] };
      return check;
    });
    return [
      ...rechecked,
      ...[...fresh.values()].filter(
        (check) => !parseChecks.some((p) => p.code === check.code),
      ),
    ];
  }, [parsed, dryRun]);

  const hasErrors = checks.some((c) => c.severity === 'error' && !c.passed);

  // The re-import's added and changed games, marked on the grid
  const marks = useMemo(() => {
    const result = new Map();
    if (!draft || !dryRun?.games) return result;
    const at = new Map(draft.games.map((game, i) => [game.key, i]));
    const mark = (key, kind) => at.has(key) && result.set(at.get(key), kind);
    for (const key of dryRun.games.created) mark(key, 'added');
    for (const { key } of dryRun.games.updated) mark(key, 'changed');
    return result;
  }, [draft, dryRun]);

  const fixGame = (index, fix) =>
    setFixes((prev) => {
      const next = { ...prev };
      if (fix) next[index] = fix;
      else delete next[index];
      return next;
    });

  const confirmRemoval = (gameId, isConfirmed) =>
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (isConfirmed) next.add(gameId);
      else next.delete(gameId);
      return next;
    });

  const refused = dryRun?.games?.refused ?? [];
  const unconfirmed = refused.filter((g) => !confirmed.has(g.gameId)).length;
  const canApply =
    !!dryRun?.games && !isChecking && !hasErrors && unconfirmed === 0;

  const apply = async () => {
    setIsApplying(true);
    try {
      const { data } = await tournamentScheduleImportApply({
        id,
        draft,
        confirmDeletes: refused
          .map((g) => g.gameId)
          .filter((gameId) => confirmed.has(gameId)),
      });
      const changes = changesText(data);
      if (changes) toast.success(`Schedule updated: ${changes}`);
      else toast.info('No changes: the event already matches this tab');
      navigate(`/tournaments/${id}#schedule`, { replace: true });
    } catch (error) {
      console.error(error);
      toast.error(error?.message ?? 'Failed to import the schedule');
      // Somebody may have assigned a referee meanwhile: show what is true now
      setDryRunTick((t) => t + 1);
    } finally {
      setIsApplying(false);
    }
  };

  const createEvent = async (event) => {
    try {
      const { data } = await tournamentScheduleImportApply({ event, draft });
      toast.success(
        `Event created with ${data.created} game` +
          (data.created === 1 ? '' : 's'),
      );
      return data;
    } catch (error) {
      console.error(error);
      if (error?.details?.checks)
        setDryRun({
          checks: indexChecks(error.details.checks, draft.games),
          failedOnly: true,
        });
      throw error;
    }
  };

  const eventForm = useMemo(() => {
    if (!parsed) return null;
    const { name, location, startDate, endDate, timezone } =
      suggestedEvent(parsed);
    return {
      id: 'new',
      name,
      location,
      startDate: startDate ? moment(startDate) : null,
      endDate: endDate ? moment(endDate) : null,
      timezone,
    };
  }, [parsed]);

  const importedAt = tournament?.source?.importedAt?.toDate?.();

  return (
    <Card
      header={id ? 'Re-import schedule' : 'New event from a schedule'}
      className='schedule-import'
      btnText='Back'
      onClick={handleBack}
      btnLeftArrow
    >
      {id && (
        <p className='target'>
          Into <Link to={`/tournaments/${id}`}>{tournament.name}</Link>
          {tournament.source &&
            `, last imported from ${tournament.source.name}, ` +
              `tab ${tournament.source.sheet}` +
              (importedAt
                ? `, ${moment(importedAt).format('MMM D, h:mma')}`
                : '')}
        </p>
      )}

      <section className='source'>
        <Fieldset label='Format' className='format'>
          {formats.map((f) => (
            <RadioButton
              key={f.id}
              label={f.name}
              name='format'
              value={f.id}
              selectedValue={format}
              onChange={pickFormat}
            />
          ))}
        </Fieldset>
        <Fieldset label='Source' className='source-kind'>
          {sourceKinds.map((kind) => (
            <RadioButton
              key={kind.id}
              label={kind.name}
              name='source'
              value={kind.id}
              selectedValue={sourceKind}
              onChange={pickSourceKind}
            />
          ))}
        </Fieldset>
        {isSheet ? (
          <div className='link'>
            <Input
              className='link-input'
              name='link'
              placeholder='Google Sheet link'
              value={link}
              onChange={(e) => setLink(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && link.trim() && !busy) openLink();
              }}
              disabled={busy}
            />
            <button
              type='button'
              className='small bw'
              onClick={openLink}
              disabled={!link.trim() || busy}
            >
              Open
            </button>
            {eventSheet && (
              <button
                type='button'
                className='small bw'
                onClick={openEventSheet}
                disabled={busy}
              >
                The event's sheet
              </button>
            )}
          </div>
        ) : (
          <div className='file'>
            <button
              type='button'
              className='small bw icon fa-upload'
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              {source ? 'Another file' : 'Choose file'}
            </button>
            <p className={'file-name' + (source ? ' font-medium' : '')}>
              {source?.name ??
                `An .${formatInfo.extensions.join(', .')} file, up to 5 MB`}
            </p>
            <input
              ref={fileInputRef}
              type='file'
              accept={formatInfo.extensions.map((x) => `.${x}`).join(',')}
              onChange={pickFile}
              hidden
            />
          </div>
        )}
        {isSheet && source && (
          <p className='file-name font-medium'>{sourceName}</p>
        )}
        {isSheet && access && (
          <div className='sheet-access'>
            {isGoogleConfigured && access.configured ? (
              <>
                <p>
                  {access.connected
                    ? 'Your Google account has not shared this sheet with Muva yet.'
                    : 'The sheet is not shared by link: connect your Google account to import it.'}
                </p>
                <button
                  type='button'
                  className='small'
                  onClick={grantSheet}
                  disabled={busy}
                >
                  {access.connected
                    ? 'Pick the sheet in Google'
                    : 'Connect Google'}
                </button>
              </>
            ) : (
              <p>
                The sheet is not shared by link. Share it as "Anyone with the
                link can view" and open it again: signing in with Google is not
                set up for Muva yet.
              </p>
            )}
          </div>
        )}
        {isSheet && !access && !isGoogleConfigured && (
          <p className='sheet-note font-12'>
            Only a sheet shared as "Anyone with the link can view" opens for
            now.
          </p>
        )}
        {isSheet && googleAccount?.connected && (
          <div className='google-account'>
            <p className='font-12'>
              Google: {googleAccount.email ?? 'connected'}
            </p>
            <button
              type='button'
              className='small bw'
              onClick={disconnectGoogle}
              disabled={busy}
            >
              Disconnect Google
            </button>
          </div>
        )}
        {source && (
          <div className='sheet-read'>
            <Select
              className='sheet'
              options={selectableSheets.map((s) => s.name)}
              value={sheet}
              onChange={setSheet}
              placeholder='Tab'
              disabled={busy}
            />
            <button
              type='button'
              className='read'
              onClick={() => read()}
              disabled={!sheet || busy}
            >
              Read schedule
            </button>
          </div>
        )}
        {hasRefsTab && (
          <p className='refs-note font-12'>
            Referee tabs are not imported: they hold people, not games.
          </p>
        )}
        <Spinner show={isListing} />
      </section>

      {isReading && <ReadingProgress sheet={sheet} />}

      {unchanged && (
        <p className='unchanged'>
          {source?.kind === 'gsheet'
            ? 'No changes: the tab holds what the event last imported.'
            : 'No changes: the event was last imported from this very file and tab.'}
        </p>
      )}

      {draft && (
        <ScheduleImportPreview
          draft={draft}
          original={parsed}
          checks={checks}
          fixes={fixes}
          marks={marks}
          onFix={fixGame}
          isChecking={isChecking}
          checkError={dryRun?.error}
        />
      )}

      {draft && id && (
        <section className='apply'>
          <ScheduleImportDiff
            tournament={tournament}
            draft={draft}
            dryRun={dryRun?.games && arePartsReady ? dryRun : null}
            isChecking={isChecking || !arePartsReady}
            confirmed={confirmed}
            onConfirm={confirmRemoval}
          />
          <div className='apply-buttons'>
            <button type='button' className='bw' onClick={handleBack}>
              Cancel
            </button>
            <button type='button' onClick={apply} disabled={!canApply || busy}>
              Apply
            </button>
          </div>
          {!isChecking && (hasErrors || unconfirmed > 0) && (
            <p className='apply-hint font-12'>
              {hasErrors
                ? 'Fix the failing error checks to apply'
                : 'Confirm each removal of a game with referees to apply'}
            </p>
          )}
          <Spinner show={isApplying} />
        </section>
      )}

      {draft && !id && (
        <section className='apply'>
          <h5>The event</h5>
          <TournamentCardEdit
            tournament={eventForm}
            teamless
            onCreate={createEvent}
            afterCreateNavPath={(data) => `/tournaments/${data.id}#schedule`}
            submitText='Create event'
            onCancel={handleBack}
            onValuesChange={(values) => {
              eventRef.current = values;
            }}
          />
        </section>
      )}
    </Card>
  );
}
